import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { Upload, X, FileAudio, FileImage, File, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { buildSafeStoragePath, validateUpload, safeContentType } from '@/lib/sanitizeFilename';

interface FileUploadSectionProps {
  accept: string;
  label: string;
  currentUrl: string | null;
  onFileUploaded: (publicUrl: string) => void;
  onClear?: () => void;
  /** Optional resource URL field */
  showResourceUrl?: boolean;
  resourceUrl?: string;
  onResourceUrlChange?: (url: string) => void;
  /** Permite subir archivos .html/.htm (recursos interactivos OVA). SVG/JS siguen bloqueados. */
  allowHtml?: boolean;
}

export function FileUploadSection({
  accept,
  label,
  currentUrl,
  onFileUploaded,
  onClear,
  showResourceUrl,
  resourceUrl,
  onResourceUrlChange,
  allowHtml,
}: FileUploadSectionProps) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const isImage = accept.startsWith('image');
  const isAudio = accept.startsWith('audio');

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // A14: validar tipo (según accept) y tamaño antes de subir; bloquear HTML/SVG/JS.
    const allow = isImage ? /^image\// : isAudio ? /^audio\// : undefined;
    const check = validateUpload(file, { allow, allowHtml });
    if (!check.ok) { toast.error(check.error); if (inputRef.current) inputRef.current.value = ''; return; }

    setUploading(true);
    try {
      const path = buildSafeStoragePath('uploads', file.name);

      const { error } = await supabase.storage.from('content').upload(path, file, {
        cacheControl: '3600',
        upsert: false,
        contentType: safeContentType(file, allowHtml),
      });
      if (error) throw error;

      const { data: urlData } = supabase.storage.from('content').getPublicUrl(path);
      onFileUploaded(urlData.publicUrl);
      toast.success('Archivo subido correctamente');
    } catch (err: any) {
      toast.error('Error al subir archivo: ' + (err.message ?? ''));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const PreviewIcon = isImage ? FileImage : isAudio ? FileAudio : File;

  return (
    <div className="space-y-3">
      <Label>{label}</Label>

      {/* Preview */}
      {currentUrl && (
        <div className="relative rounded-md border border-border p-2">
          {isImage ? (
            <img src={currentUrl} alt="Preview" className="max-h-40 rounded object-contain mx-auto" />
          ) : (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <PreviewIcon className="h-5 w-5" />
              <span className="truncate flex-1">{currentUrl.split('/').pop()}</span>
            </div>
          )}
          {onClear && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute top-1 right-1 h-6 w-6"
              onClick={onClear}
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>
      )}

      {/* Upload button */}
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
        >
          {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
          {uploading ? 'Subiendo...' : 'Subir archivo'}
        </Button>
        <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={handleUpload} />
      </div>

      {/* Optional resource URL */}
      {showResourceUrl && (
        <div className="grid gap-2">
          <Label className="text-xs text-muted-foreground">URL del recurso (opcional)</Label>
          <Input
            value={resourceUrl ?? ''}
            onChange={(e) => onResourceUrlChange?.(e.target.value)}
            placeholder="https://..."
          />
        </div>
      )}
    </div>
  );
}
